
import { FilterButton } from "../base/FilterButton"
import { MultiSelectFilter } from "../base/MultiSelectFilter"
import { SearchableSelectFilter } from "../base/SearchableSelectFilter"
import { SearchFilter } from "../base/SearchFilter"
import {
  BankFilter,
  ChannelFilter,
  CurrencyFilter,
  MerchantFilter,
  OrganizationFilter,
  PaymentMethodFilter,
  ProcessorFilter,
  ProductPlatformFilter,
  ProgramFilter,
} from "./FilterFactory"
import { AmountFilterDropdown } from "./amount"
import { DateFilter } from "./date"
import { CustomerFilters } from "./customer"
import { StoreFilters } from "./terminal/StoreFilters"
import { LocationFilters } from "./terminal/LocationFilters"
import type { AmountFilterValue } from "./amount"
import type { DateFilterValue } from "@shared/types/dateFilter"
import type { FilterConfig } from "@shared/types/filters"
import { isValidObjectWithOperator } from "@shared/utils"

type FilterRenderer = (
  filter: FilterConfig,
  value: unknown,
  onChange: (value: unknown) => void
) => React.ReactElement

const renderAmountFilter: FilterRenderer = (filter, value, onChange) => (
  <AmountFilterDropdown
    key={filter.key}
    value={isValidObjectWithOperator(value) ? value as AmountFilterValue : null}
    onChange={onChange}
    filterConfig={filter}
  />
)

const renderDateFilter: FilterRenderer = (filter, value, onChange) => (
  <DateFilter
    key={filter.key}
    value={isValidObjectWithOperator(value) ? value as DateFilterValue : null}
    label={filter.label}
    onChange={onChange}
  />
)


const SEARCHABLE_MULTI_SELECT_RENDERERS: Record<string, FilterRenderer> = {
  customers: (filter, value, onChange) => (
    <CustomerFilters key={filter.key} customersValue={value as string[]} onCustomersChange={onChange} />
  ),
  store: (filter, value, onChange) => (
    <StoreFilters key={filter.key} storesValue={value as string[]} onStoresChange={onChange} />
  ),
  location: (filter, value, onChange) => (
    <LocationFilters key={filter.key} locationsValue={value as string[]} onLocationsChange={onChange} />
  ),
}

const renderSearchableMultiSelectFilter: FilterRenderer = (filter, value, onChange) => {
  const renderer = SEARCHABLE_MULTI_SELECT_RENDERERS[filter.key]
  if (renderer) return renderer(filter, value, onChange)

  return (
    <MultiSelectFilter
      key={filter.key}
      options={filter.options || []}
      value={value as string[]}
      onChange={onChange}
      filterConfig={filter}
    />
  )
}

interface FilterComponentProps {
  value: string | string[] | null | undefined
  onChange: (value: unknown) => void
  filterConfig: FilterConfig
}

const _createSingleSelectRenderer = (FilterComponent: React.ComponentType<FilterComponentProps>): FilterRenderer =>
  (filter, value, onChange) => (
    <FilterComponent
      key={filter.key}
      value={Array.isArray(value) ? value[0] : value as string}
      onChange={onChange}
      filterConfig={filter}
    />
  )

const createMultiSelectRenderer = (FilterComponent: React.ComponentType<FilterComponentProps>): FilterRenderer =>
  (filter, value, onChange) => (
    <FilterComponent
      key={filter.key}
      value={value as string[]}
      onChange={onChange}
      filterConfig={filter}
    />
  )

const renderMultiSelectFilter: FilterRenderer = (filter, value, onChange) => (
  <MultiSelectFilter
    key={filter.key}
    options={filter.options || []}
    value={value as string[]}
    onChange={onChange}
    filterConfig={filter}
  />
)

const renderSelectFilter: FilterRenderer = (filter, value, onChange) => (
  <SearchableSelectFilter
    key={filter.key}
    options={filter.options || []}
    value={value as string | null}
    onChange={onChange as (value: string | null) => void}
    filterConfig={filter}
  />
)

const renderSearchFilter: FilterRenderer = (filter, value, onChange) => (
  <SearchFilter
    key={filter.key}
    value={value as string | null}
    onChange={onChange as (value: string | null) => void}
    filterConfig={filter}
  />
)

export const FILTER_RENDERERS: Record<string, FilterRenderer> = {
  advancedAmount: renderAmountFilter,
  amountRange: renderAmountFilter,
  dateRange: renderDateFilter,
  searchableMultiSelect: renderSearchableMultiSelectFilter,
  
  currency: createMultiSelectRenderer(CurrencyFilter),
  paymentMethod: createMultiSelectRenderer(PaymentMethodFilter),
  
  program: createMultiSelectRenderer(ProgramFilter),
  merchant: createMultiSelectRenderer(MerchantFilter),
  bank: createMultiSelectRenderer(BankFilter),
  organization: createMultiSelectRenderer(OrganizationFilter),
  productPlatform: createMultiSelectRenderer(ProductPlatformFilter),
  processor: createMultiSelectRenderer(ProcessorFilter),
  channel: createMultiSelectRenderer(ChannelFilter),
  
  multiSelect: renderMultiSelectFilter,
  status: renderMultiSelectFilter,
  select: renderSelectFilter,
  search: renderSearchFilter,
  text: renderSearchFilter,
}

/**
 * Generic filter renderer that handles fallback to FilterButton with unsupported message
 */
export function renderGenericFilter(
  filter: FilterConfig,
  value: unknown,
  isOpen: boolean,
  onToggle: (open: boolean) => void,
  onClear: () => void,
  displayValue: { label: string; displayValue: string }
) {
  return (
    <FilterButton
      key={filter.key}
      filter={filter}
      isActive={!!value}
      isOpen={isOpen}
      displayValue={displayValue}
      onToggle={onToggle}
      onClear={onClear}
    >
      <div className="p-3">
        <p className="text-xs text-muted-foreground">
          Filter type "{filter.type}" not implemented yet
        </p>
      </div>
    </FilterButton>
  )
}
